/**
 * Vincent B931 (Eric 2026-02-14 SPEC STRICK PROD) — Modal « Étiqueter
 * la sélection » : applique une liste de tags canoniques (choisis dans
 * le vocabulaire de l'organisation ou créés à la volée) à N documents
 * ou vidéos sélectionnés dans l'Explorateur.
 *
 * Mode par défaut : ADDITIF (les tags existants sont préservés).
 * Toggle « Remplacer les tags existants » : mode DESTRUCTIF —
 * nécessite une confirmation avant application.
 *
 * L'endpoint `/api/videos/{id}/tags/accept` accepte `{tags:[...],
 * replace:bool}` — B931 backend étend le endpoint existant plutôt
 * que d'introduire une nouvelle route.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Tag, Plus, SpinnerGap, Warning } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { API_BASE as API } from "../../lib/apiBase";

export const BatchTagsModal = ({
  open,
  onOpenChange,
  selectedIds = [],
  videosById = {},
  onApplied,
}) => {
  const [orgVocab, setOrgVocab] = useState([]); // [{tag, count}]
  const [loadingVocab, setLoadingVocab] = useState(false);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState([]); // string[]
  const [replaceMode, setReplaceMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, ko: 0 });
  const inputRef = useRef(null);

  // Charge le vocabulaire org une fois à l'ouverture.
  useEffect(() => {
    if (!open) return;
    setPicked([]);
    setQuery("");
    setReplaceMode(false);
    setProgress({ done: 0, ko: 0 });
    setLoadingVocab(true);
    axios
      .get(`${API}/api/tags/org-vocabulary`, { withCredentials: true })
      .then(({ data }) => setOrgVocab(Array.isArray(data?.tags) ? data.tags : []))
      .catch(() => setOrgVocab([]))
      .finally(() => setLoadingVocab(false));
  }, [open]);

  const pickedNorm = useMemo(
    () => new Set(picked.map((t) => t.toLowerCase())),
    [picked],
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orgVocab.slice(0, 30);
    return orgVocab
      .filter((v) => (v.tag || "").toLowerCase().includes(q))
      .slice(0, 30);
  }, [orgVocab, query]);

  const canCreateNew = useMemo(() => {
    const q = query.trim();
    if (!q) return false;
    const qn = q.toLowerCase();
    // Existe déjà dans le vocabulaire OU déjà picked → on ne "crée" pas.
    if (pickedNorm.has(qn)) return false;
    if (orgVocab.some((v) => (v.tag || "").toLowerCase() === qn)) return false;
    return true;
  }, [query, orgVocab, pickedNorm]);

  const addTag = useCallback((tag) => {
    const t = String(tag || "").trim();
    if (!t) return;
    setPicked((prev) => (prev.some((x) => x.toLowerCase() === t.toLowerCase()) ? prev : [...prev, t]));
    setQuery("");
    inputRef.current?.focus();
  }, []);

  const removeTag = (tag) => {
    setPicked((prev) => prev.filter((t) => t.toLowerCase() !== tag.toLowerCase()));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const q = query.trim();
      if (!q) return;
      // Si un match exact existe (case-insensitive), on l'ajoute ; sinon
      // on crée le nouveau tag.
      const exact = orgVocab.find((v) => (v.tag || "").toLowerCase() === q.toLowerCase());
      addTag(exact?.tag || q);
    } else if (e.key === "," || e.key === ";") {
      e.preventDefault();
      const q = query.trim();
      if (q) addTag(q);
    } else if (e.key === "Backspace" && !query && picked.length > 0) {
      // Retire le dernier tag picked au backspace dans l'input vide.
      setPicked((prev) => prev.slice(0, -1));
    }
  };

  const applyBatch = async () => {
    if (picked.length === 0 || selectedIds.length === 0 || submitting) return;
    if (replaceMode) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        `⚠️ MODE REMPLACEMENT : les tags existants seront ÉCRASÉS sur ${selectedIds.length} document(s). Continuer ?`,
      );
      if (!ok) return;
    }
    // Vincent B932 (Eric 2026-02-14 SPEC STRICK PROD) — Undo Last Retag.
    // Capture les tags courants AVANT l'apply pour permettre un rollback
    // en 1 clic pendant 60s (via un toast avec action). Les docs qui
    // n'apparaissent pas dans videosById sont snapshotés à [] — c'est
    // safe : le rollback les remettra à [] via replace-mode.
    const snapshot = selectedIds.map((vid) => ({
      id: vid,
      tags: Array.isArray(videosById?.[vid]?.tags) ? [...videosById[vid].tags] : [],
    }));
    setSubmitting(true);
    setProgress({ done: 0, ko: 0 });
    let done = 0, ko = 0;
    for (const vid of selectedIds) {
      try {
        await axios.post(
          `${API}/api/videos/${vid}/tags/accept`,
          { tags: picked, replace: replaceMode },
          { withCredentials: true },
        );
        done++;
      } catch {
        ko++;
      }
      setProgress({ done, ko });
    }
    setSubmitting(false);
    const successMsg = ko === 0
      ? `✅ ${done} document(s) ${replaceMode ? "retagués" : "étiquetés"} avec « ${picked.join(", ")} »`
      : `${done} OK · ${ko} en erreur — voir la console pour détails.`;
    // Vincent B932 — Toast avec action « Annuler » (60s). Le rollback
    // fait un replace-mode avec le snapshot pour chaque doc — idempotent
    // et sûr (aucun toast d'undo n'est proposé si zéro doc a réussi).
    if (done > 0) {
      const toastFn = ko === 0 ? toast.success : toast.warning;
      toastFn(successMsg, {
        duration: 60000, // 60s pour cliquer sur Annuler
        action: {
          label: "Annuler",
          onClick: () => performUndo(snapshot),
        },
      });
    } else {
      toast.warning(successMsg);
    }
    if (typeof onApplied === "function") {
      try { await onApplied(); } catch { /* ignore */ }
    }
    onOpenChange(false);
  };

  // Vincent B932 (Eric 2026-02-14 SPEC STRICK PROD) — Rollback batch retag.
  // Ré-écrit `tags[]` de chaque doc affecté avec sa liste d'origine via
  // replace-mode. Cette fonction est appelée depuis l'action du toast
  // succès/warning (60s de fenêtre). Silencieux sur erreur DB pour ne pas
  // spammer Vincent — il verra dans la liste si un doc n'a pas été rollback.
  const performUndo = async (snapshot) => {
    if (!Array.isArray(snapshot) || snapshot.length === 0) return;
    let undone = 0, ko = 0;
    for (const s of snapshot) {
      try {
        await axios.post(
          `${API}/api/videos/${s.id}/tags/accept`,
          { tags: s.tags || [], replace: true },
          { withCredentials: true },
        );
        undone++;
      } catch {
        ko++;
      }
    }
    if (ko === 0) {
      toast.success(`↩️ Rollback : tags d'origine restaurés sur ${undone} document(s).`);
    } else {
      toast.warning(`↩️ Rollback : ${undone} OK · ${ko} en erreur.`);
    }
    if (typeof onApplied === "function") {
      try { await onApplied(); } catch { /* ignore */ }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="batch-tags-modal">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <Tag size={18} weight="duotone" className="text-[#002FA7]" />
            Étiqueter {selectedIds.length} document(s)
          </DialogTitle>
          <DialogDescription>
            Choisir un ou plusieurs tags depuis le vocabulaire de votre organisation, ou en créer un nouveau. Idempotent : les tags déjà présents ne sont pas dupliqués.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Chips des tags picked */}
          {picked.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pb-1 border-b border-zinc-200" data-testid="batch-tags-picked-list">
              {picked.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#002FA7]/10 text-[#002FA7] text-xs font-semibold"
                  data-testid={`batch-tags-picked-${t}`}
                >
                  {t}
                  <button
                    type="button"
                    onClick={() => removeTag(t)}
                    className="hover:text-rose-600"
                    aria-label={`Retirer ${t}`}
                    data-testid={`batch-tags-remove-${t}`}
                  >
                    <X size={11} weight="bold" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Input recherche + autocomplete */}
          <div>
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Rechercher un tag existant ou en saisir un nouveau (Entrée pour valider)"
              disabled={submitting}
              data-testid="batch-tags-input"
              autoFocus
            />
            <p className="mt-1 text-[10px] text-zinc-500">
              {loadingVocab
                ? "Chargement du vocabulaire…"
                : `${orgVocab.length} tag(s) dans le vocabulaire org · Entrée = ajouter · Backspace = retirer le dernier`}
            </p>
          </div>

          {/* Liste des matches (autocomplete) */}
          <div className="border border-zinc-200 rounded-lg max-h-48 overflow-y-auto" data-testid="batch-tags-matches">
            {matches.length === 0 && !canCreateNew && (
              <div className="px-3 py-4 text-center text-xs text-zinc-400">
                {query.trim() ? "Aucun tag correspondant" : "Le vocabulaire est vide"}
              </div>
            )}
            {canCreateNew && (
              <button
                type="button"
                onClick={() => addTag(query.trim())}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-teal-50 border-b border-zinc-100"
                data-testid="batch-tags-create-new"
              >
                <span className="inline-flex items-center gap-2 text-sm text-teal-800 font-semibold">
                  <Plus size={13} weight="bold" />
                  Créer « {query.trim()} »
                </span>
                <span className="text-[10px] text-teal-600 uppercase tracking-wider font-bold">Nouveau</span>
              </button>
            )}
            {matches.map((v) => {
              const already = pickedNorm.has((v.tag || "").toLowerCase());
              return (
                <button
                  key={v.tag}
                  type="button"
                  onClick={() => addTag(v.tag)}
                  disabled={already}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left border-b border-zinc-100 last:border-0 ${
                    already ? "bg-zinc-50 text-zinc-400 cursor-not-allowed" : "hover:bg-zinc-50"
                  }`}
                  data-testid={`batch-tags-vocab-${v.tag}`}
                >
                  <span className="text-sm font-medium">{v.tag}</span>
                  <span className="text-[10px] text-zinc-500">
                    {already ? "déjà ajouté" : `${v.count || 0} vid.`}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Toggle Replace */}
          <label
            className="flex items-start gap-2 px-3 py-2 rounded-lg bg-rose-50/60 border border-rose-200 cursor-pointer"
            data-testid="batch-tags-replace-toggle-label"
          >
            <input
              type="checkbox"
              checked={replaceMode}
              onChange={(e) => setReplaceMode(e.target.checked)}
              disabled={submitting}
              className="mt-0.5 w-4 h-4 accent-rose-600"
              data-testid="batch-tags-replace-toggle"
            />
            <span className="text-xs text-rose-900 leading-snug">
              <strong>Remplacer les tags existants</strong> — écrase la liste actuelle sur chaque document sélectionné (destructif, confirmation requise).
            </span>
          </label>

          {/* Progress live pendant apply */}
          {submitting && (
            <div className="text-xs text-zinc-600 flex items-center gap-2" data-testid="batch-tags-progress">
              <SpinnerGap size={12} className="animate-spin" />
              {progress.done + progress.ko} / {selectedIds.length} traité(s) · {progress.done} OK
              {progress.ko > 0 && (
                <span className="inline-flex items-center gap-1 text-rose-600 font-semibold">
                  <Warning size={11} /> {progress.ko} erreur(s)
                </span>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="batch-tags-cancel"
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={applyBatch}
            disabled={picked.length === 0 || selectedIds.length === 0 || submitting}
            data-testid="batch-tags-apply"
          >
            {submitting && <SpinnerGap size={14} className="animate-spin mr-1.5" />}
            {replaceMode
              ? `Remplacer sur ${selectedIds.length}`
              : `Ajouter à ${selectedIds.length} document(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BatchTagsModal;
